import { isPlainObject } from 'lodash';
import { ProductTypes } from '../types';

/**
 * Product keys that stand for the same offering and must always be filtered together.
 *
 * Prime 250 is one product to everyone who chooses or filters a product; P250 and P250X
 * are two internal keys behind it (FPX-8518). A filter that names only P250 — a chip, a
 * saved filter, or a bookmarked URL, all authored before P250X existed — must still
 * return the whole Prime 250 book rather than half of it.
 *
 * Transitional: when P250 retires, delete this entry.
 */
const PRODUCT_KEY_GROUPS: Readonly<Record<string, readonly string[]>> = {
  [ProductTypes.p250]: [ProductTypes.p250, ProductTypes.p250x],
};

/**
 * The key the platform lists an offering under, for a product key that is one of its group: P250 for P250X. A product
 * in no group is listed as itself. The supported-products tree lists P250, which P250X is quoted as.
 *
 * Rating depends on this (`linesForRating`), so retiring P250 means updating the rating too.
 */
export const treeProductOf = (productType: string): string =>
  Object.keys(PRODUCT_KEY_GROUPS).find((listed) =>
    PRODUCT_KEY_GROUPS[listed].includes(productType)
  ) ?? productType;

// The column whose keys group, and the operators that take a comma-joined key list.
// Every other operator compares a single scalar, where a comma-joined value would be
// meaningless rather than wider.
const EXPANDABLE_COLUMN = 'product';
const EXPANDABLE_OPERATORS = ['in', 'ni'];

// The same column and operators in the flattened spelling the write path sees.
const EXPANDABLE_PARAMS = EXPANDABLE_OPERATORS.map(
  (operator) => `f[${EXPANDABLE_COLUMN}][${operator}]`
);

const expandKeys = (value: string) => {
  const expanded = value
    .split(',')
    .flatMap((key) => PRODUCT_KEY_GROUPS[key] ?? [key]);

  // Array.from, not [...new Set()] — this app's jest transform turns a spread
  // Set into an empty array rather than its members, silently.
  return Array.from(new Set(expanded)).join(',');
};

/**
 * Widen a product filter value whether it arrived as a comma-joined string or as an
 * array from bracket-array query syntax (`f[product][in][]=P250`).
 */
const expandFilterValue = (value: unknown) => {
  if (typeof value === 'string') {
    return expandKeys(value);
  }

  if (Array.isArray(value)) {
    const keys = value.filter((key): key is string => typeof key === 'string');
    return expandKeys(keys.join(','));
  }

  return value;
};

const isExpandableFilterValue = (value: unknown) =>
  typeof value === 'string' || Array.isArray(value);

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return isPlainObject(value);
}

/**
 * Widen any grouped product key in a flattened filter param map to its whole group.
 *
 * Applied where every filter is written to the URL — the filter modal, the chips and
 * the saved-filter menus all write through `flattenQuery` — so a stored filter naming
 * one key of a group does not silently narrow the results.
 *
 * This is the *write* half. A filter read back off an already-formed URL never passes
 * through here; see {@link withExpandedProductKeysInFilter}.
 */
export const withExpandedProductKeys = (params: Record<string, string>) =>
  Object.keys(params).reduce<Record<string, string>>((acc, param) => {
    const value = params[param];
    const isExpandable =
      EXPANDABLE_PARAMS.includes(param) && typeof value === 'string';

    return { ...acc, [param]: isExpandable ? expandKeys(value) : value };
  }, {});

/**
 * Widen any grouped product key in a *nested* filter object to its whole group.
 *
 * The read half, and the counterpart to {@link withExpandedProductKeys}. A filter on
 * its way to the URL is flattened first, so the write path sees `f[product][in]` as a
 * flat param key. A filter read back off an already-formed URL — a bookmark, a link
 * pasted in Slack, a refresh, a browser back-navigation — is never flattened: it
 * arrives as the nested object `qs.parse` produces, `{ product: { in: 'P250' } }`, and
 * goes straight out as `filter[product][in]`. Without this it reached the API
 * un-widened and returned half the Prime 250 book, silently (FPX-8863).
 *
 * Anything that is not a product filter is returned untouched, so this is safe to
 * apply to every listing's filter object regardless of its columns.
 */
export const withExpandedProductKeysInFilter = (filter: unknown) => {
  if (!isPlainRecord(filter)) {
    return filter;
  }

  const productFilter = filter[EXPANDABLE_COLUMN];

  // Bare product value without an operator: ?f[product]=P250 or ?f[product][]=P250
  if (isExpandableFilterValue(productFilter)) {
    return {
      ...filter,
      [EXPANDABLE_COLUMN]: expandFilterValue(productFilter),
    };
  }

  if (!isPlainRecord(productFilter)) {
    return filter;
  }

  const expandedProductFilter = Object.keys(productFilter).reduce<
    Record<string, unknown>
  >((acc, operator) => {
    const value = productFilter[operator];
    const isExpandable =
      EXPANDABLE_OPERATORS.includes(operator) && isExpandableFilterValue(value);

    return {
      ...acc,
      [operator]: isExpandable ? expandFilterValue(value) : value,
    };
  }, {});

  return { ...filter, [EXPANDABLE_COLUMN]: expandedProductFilter };
};
